import { Query, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useMemo } from 'react';
import { generatePath } from 'react-router';
import { LandEvent } from '~/components/Land/Event';
import { useMember } from '~/components/Member/Member.hooks';
import { UseDosiminQueryOptions } from '~/components/Query/Query.hooks';
import { sharedDateTimer } from '~/components/Util';

export type ZoneType = 'ZONE_1' | 'ZONE_2' | 'ZONE_3';
export type LandType = 'GOLDEN_GALAXY' | 'SAPPHIRE_STARLIGHT' | 'EMERALD_ORBIT';
export type LandStatus = 'END' | 'IN_PROGRESS' | 'UPCOMING';
export type W8benStatus = 'REGISTERED' | 'UNREGISTERED' | 'DISAGREE' | 'EXPIRED';

export interface LandZone {
  zoneId: number;
  zoneType: ZoneType;
  isParticipatable: boolean;
  rewardAmount: number;
  rewardCurrency: string;
  requiredAsset: {
    // DON
    assetType: string;
    amount: number;
  };
  participationStatus: {
    participantsCount: number;
    winnerCount: number;
    citizenNftCount: number;
    levelUpPassCount: number;
  };
  requiredLevelUpPassCount: number;
  // transformed data
  zoneNumber: number;
}

export interface Land {
  landId: number;
  week: number;
  landType: LandType;
  startAt: Date;
  endAt: Date;
  announcedAt: Date;
  landStatus: LandStatus;
  citizenNftLevel: number;
  myParticipation?: {
    participatedAt: Date;
    zoneId: number;
    zoneType: ZoneType;
    zoneNumber: number;
    citizenNftCount: number;
    levelUpPassCount: number;
  };
  myHoldings: {
    asset: {
      assetType: 'DON' | string;
      amount: number;
    };
    levelUpPassCount: number;
    isHolderOfCitizenNft: boolean;
  };
  zoneList: LandZone[];
  // transformed data
  isParticipated: boolean;
  // transformed data
  participationStatus: {
    participantsCount: number;
    winnerCount: number;
    citizenNftCount: number;
    levelUpPassCount: number;
  };
}

export const initialZoneList: LandZone[] = [
  {
    zoneId: 1,
    zoneType: 'ZONE_1',
    isParticipatable: false,
    rewardAmount: 0,
    rewardCurrency: 'FNSA',
    requiredAsset: {
      assetType: 'DON',
      amount: 0,
    },
    participationStatus: {
      participantsCount: 0,
      winnerCount: 0,
      citizenNftCount: 0,
      levelUpPassCount: 0,
    },
    requiredLevelUpPassCount: 0,
    zoneNumber: 1,
  },
  {
    zoneId: 2,
    zoneType: 'ZONE_2',
    isParticipatable: false,
    rewardAmount: 0,
    rewardCurrency: 'FNSA',
    requiredAsset: {
      assetType: 'DON',
      amount: 0,
    },
    participationStatus: {
      participantsCount: 0,
      winnerCount: 0,
      citizenNftCount: 0,
      levelUpPassCount: 0,
    },
    requiredLevelUpPassCount: 0,
    zoneNumber: 2,
  },
  {
    zoneId: 3,
    zoneType: 'ZONE_3',
    isParticipatable: false,
    rewardAmount: 0,
    rewardCurrency: 'FNSA',
    requiredAsset: {
      assetType: 'DON',
      amount: 0,
    },
    participationStatus: {
      participantsCount: 0,
      winnerCount: 0,
      citizenNftCount: 0,
      levelUpPassCount: 0,
    },
    requiredLevelUpPassCount: 0,
    zoneNumber: 3,
  },
];

export const landUrl = '/api/citizen/v1/lands/:id';

export const initialParticipationStatus = {
  participantsCount: 0,
  winnerCount: 0,
  citizenNftCount: 0,
  levelUpPassCount: 0,
};

export const initialMyHoldings = {
  asset: {
    assetType: 'DON',
    amount: 0,
  },
  levelUpPassCount: 0,
  isHolderOfCitizenNft: false,
};

export const transformLand = (response: Land) => ({
  ...response,
  startAt: new Date(response.startAt),
  endAt: new Date(response.endAt),
  announcedAt: new Date(response.announcedAt),
  myParticipation: response.myParticipation
    ? {
        ...response.myParticipation,
        zoneNumber: Number(response.myParticipation.zoneType.split('_')[1]),
        participatedAt: new Date(response.myParticipation.participatedAt),
      }
    : undefined,
  isParticipated: !!response.myParticipation,
  zoneList: response.zoneList.map((zone) => ({
    ...zone,
    zoneNumber: Number(zone.zoneType.split('_')[1]),
  })),
  participationStatus: response.zoneList.reduce(
    (prev, { participationStatus }) => ({
      participantsCount: prev.participantsCount + participationStatus.participantsCount,
      winnerCount: prev.winnerCount + participationStatus.winnerCount,
      citizenNftCount: prev.citizenNftCount + participationStatus.citizenNftCount,
      levelUpPassCount: prev.levelUpPassCount + participationStatus.levelUpPassCount,
    }),
    initialParticipationStatus
  ),
});
export const requestLand = async (id: number) => {
  const { data } = await axios.get<Land>(generatePath(landUrl, { id }));
  return transformLand(data);
};

const initialLandData: Land = {
  week: 0,
  landId: 0,
  citizenNftLevel: 0,
  isParticipated: false,
  landStatus: 'END',
  landType: 'EMERALD_ORBIT',
  myHoldings: initialMyHoldings,
  participationStatus: initialParticipationStatus,
  startAt: sharedDateTimer.now,
  endAt: sharedDateTimer.now,
  announcedAt: sharedDateTimer.now,
  zoneList: initialZoneList,
};

export const useLand = (id: number, options?: UseDosiminQueryOptions<Land>) => {
  const client = useQueryClient();
  const filter = useMemo(
    () => ({
      queryKey: ['land', 'event'],
      exact: false,
      predicate: (query: Query) => {
        if (!Number.isInteger(query.queryKey[2])) return false;
        return !!(query as Query<LandEvent>).state.data?.landList.some(
          ({ landId }) => landId === id
        );
      },
    }),
    [id]
  );
  return useQuery<Land>(['land', id], () => requestLand(id), {
    initialData: (): Land => {
      const state = client.getQueryState<LandEvent>(filter.queryKey, filter);
      const { landId, landType, citizenNftLevel, isParticipated } =
        state?.data?.landList.find(({ landId }) => landId === id) || initialLandData;

      return {
        ...initialLandData,
        landId: landId || id,
        landType,
        citizenNftLevel,
        isParticipated,
      };
    },
    initialDataUpdatedAt: () => {
      const state = client.getQueryState<LandEvent>(filter.queryKey, filter);
      return state?.dataUpdatedAt || 0;
    },
    ...options,
  });
};

export const restrictedRegions = ['US', 'JP'];

export const useRestrictedRegion = () => {
  const {
    data: { region },
  } = useMember();
  return region && restrictedRegions.includes(region);
};
